import React from 'react';

// The exact SVG for the flowchart lines and flag, adjusted for PDF rendering without transforms
// Width increased to 325px (250 * 1.3) and height to 520px to match the scale(1.3) visually
const CircleLinesSvg = () => (
    <svg width="325" height="520" viewBox="0 0 250 400" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={{ marginTop: '-20px' }}>
        {/* Flag - keeping as is since it's perfect */}
        <path
            d="M83.282 69.995C85.9521 67.3778 89.5419 65.9118 93.2807 65.9118C97.0196 65.9118 100.6094 67.3778 103.2794 69.995C105.9495 72.6122 109.5393 74.0781 113.2782 74.0781C117.017 74.0781 120.6068 72.6122 123.2769 69.995V44.284C120.6068 46.9012 117.017 48.3671 113.2782 48.3671C109.5393 48.3671 105.9495 46.9012 103.2794 44.284C100.6094 41.6668 97.0196 40.2008 93.2807 40.2008C89.5419 40.2008 85.9521 41.6668 83.282 44.284V69.995ZM83.282 69.995V89.9924"
            stroke="#ED701E" strokeWidth="1.85305" strokeLinecap="round" strokeLinejoin="round" />

        {/* Circle 1 - below flag */}
        <circle cx="82.5" cy="98" r="7.87545" fill="#ED701E" />

        {/* Circle 2 - right side */}
        <circle cx="170" cy="155" r="7.87545" fill="#ED701E" />

        {/* Circle 3 - left side */}
        <circle cx="50" cy="235" r="7.87545" fill="#ED701E" />

        {/* Circle 4 - right bottom */}
        <circle cx="185" cy="325" r="7.87545" fill="#ED701E" />

        {/* Circle 5 - left bottom */}
        <circle cx="85" cy="395" r="7.87545" fill="#ED701E" />

        {/* Path from Circle 1 to Circle 2 */}
        <path
            d="M90 98 C 120 98, 140 125, 162 155"
            stroke="#ED701E" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* Path from Circle 2 to Circle 3 */}
        <path
            d="M162 155 C 140 185, 100 205, 58 235"
            stroke="#ED701E" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* Path from Circle 3 to Circle 4 */}
        <path
            d="M58 235 C 100 265, 140 295, 177 325"
            stroke="#ED701E" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* Path from Circle 4 to Circle 5 */}
        <path
            d="M177 325 C 140 355, 120 375, 93 395"
            stroke="#ED701E" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
);

// Reusable component for the content boxes - matching reference design
const TextBox = ({ title, content, items = [], style }) => (
    <div style={{
        border: '2px solid #ED701E',
        borderRadius: '20px',
        backgroundColor: '#FCE8DE',
        padding: '20px 15px',
        fontSize: '13px',
        color: '#333',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        ...style
    }}>
        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '15px' }}>{title}</div>
        {content && <div style={{ fontWeight: 'normal', fontSize: '13px' }}>{content}</div>}
        {items && items.length > 0 && (
            <div style={{ width: '100%', textAlign: 'left', paddingLeft: '10px' }}>
                {items.map((item, index) => (
                    <div key={index} style={{ marginTop: '8px', fontSize: '13px' }}>
                        {item || '\u00A0'}
                    </div>
                ))}
            </div>
        )}
    </div>
);

// Reusable component for the fill-in-the-blank lines
const LabeledLine = ({ label, value, endLabel = '' }) => (
    <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        width: '100%',
        marginBottom: '18px',
        fontSize: '14px',
        color: '#333',
        fontWeight: 400
    }}>
        <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{
            borderBottom: '1.5px solid black',
            flexGrow: 1,
            margin: '0 8px',
            textAlign: 'center',
            fontWeight: value ? 'bold' : 'normal',
            color: value ? '#3B82F6' : '#333',
            paddingBottom: '2px',
            minWidth: '150px'
        }}>
            {value || ''}
        </span>
        {endLabel && <span style={{ whiteSpace: 'nowrap' }}>{endLabel}</span>}
    </div>
);

const PartA3 = ({ data }) => {
    // Use actual data from backend
    const safeData = data || {};

    // Map backend data to component fields
    const mappedData = {
        ambition: safeData.ambition || safeData.career_aspirations?.[0] || 'My Career Aspiration',
        skills_to_achieve: safeData.skills_to_achieve || safeData.learning_goals || ['Skill 1', 'Skill 2', 'Skill 3', 'Skill 4', 'Skill 5'],
        achieve_by: safeData.achieve_by || 'working hard and staying focused.',
        focus_subjects: safeData.focus_subjects || safeData.personal_interests || ['Subject 1', 'Subject 2', 'Subject 3'],
        habits_for_ambition: safeData.habits_for_ambition || ['Good Habit 1', 'Good Habit 2', 'Good Habit 3'],
        guidance_from: safeData.guidance_from || 'my teacher',
        help_by_person: safeData.help_by_person || 'providing guidance and support.',
        learn_new: safeData.learn_new || 'skills and knowledge.',
        feel_when_achieved: safeData.feel_when_achieved || 'proud and accomplished',
        parents_feel_when_achieved: safeData.parents_feel_when_achieved || 'very proud'
    };

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.6', // Increased line-height for more vertical space
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            '@media print': {
                margin: 0,
                padding: '15mm 20mm',
                boxShadow: 'none',
                width: '210mm',
                height: '297mm'
            }
        }}>
            {/* Orange corner triangles */}
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 80px 80px 0',
                borderColor: 'transparent #ED701E transparent transparent'
            }} />
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '80px 0 0 80px',
                borderColor: 'transparent transparent transparent #ED701E'
            }} />

            <div style={{ position: 'relative', zIndex: 10, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header Section */}
                <header className="text-center" style={{ marginTop: '20px' }}>
                    <h2 style={{ color: '#e36d0b', fontWeight: 700, fontSize: '20px' }}>PART A (3)</h2>
                    <h1 style={{ fontWeight: 500, fontSize: '16px', marginTop: '5px' }}>My Ambition Card</h1>
                </header>

                {/* My Ambition Line */}
                <div style={{
                    fontWeight: 500,
                    fontSize: '16px',
                    marginTop: '20px',
                    borderBottom: '1.5px solid black',
                    paddingBottom: '4px',
                }}>
                    My Ambition is{' '}
                    <span style={{ fontWeight: 'bold', color: '#3B82F6' }}>{mappedData.ambition}</span>
                </div>
                <div style={{
                    borderBottom: '1.5px solid black',
                    height: '1.5px',
                    marginTop: '20px',
                    marginBottom: '35px'
                }} />

                {/* Main Content Area with Flowchart */}
                <div style={{
                    position: 'relative',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '20px',
                    width: '100%'
                }}>
                    {/* Container for flowchart and text boxes - Fixed width to fit page */}
                    <div style={{
                        position: 'relative',
                        width: '640px',
                        height: '450px'
                    }}>
                        {/* SVG Flowchart centered */}
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 0
                        }}>
                            <CircleLinesSvg />
                        </div>

                        {/* Text Box 1 - 5 skills (left top) */}
                        <TextBox
                            title="5 skills I need to achieve my ambition"
                            items={mappedData.skills_to_achieve}
                            style={{
                                position: 'absolute',
                                left: '0px',
                                top: '40px',
                                width: '200px',
                                minHeight: '180px',
                                zIndex: 1
                            }}
                        />

                        {/* Text Box 2 - I will achieve (right top) */}
                        <TextBox
                            title="I will achieve my ambition by"
                            content={mappedData.achieve_by}
                            style={{
                                position: 'absolute',
                                right: '0px',
                                top: '60px',
                                width: '210px',
                                minHeight: '80px',
                                zIndex: 1
                            }}
                        />

                        {/* Text Box 3 - Habits (left bottom) */}
                        <TextBox
                            title="To achieve my ambition, I need my habits to be"
                            items={mappedData.habits_for_ambition}
                            style={{
                                position: 'absolute',
                                left: '10px',
                                bottom: '-20px',
                                width: '220px',
                                minHeight: '120px',
                                zIndex: 1
                            }}
                        />

                        {/* Text Box 4 - Subjects (right bottom) */}
                        <TextBox
                            title="Subjects I need to focus on"
                            items={mappedData.focus_subjects}
                            style={{
                                position: 'absolute',
                                right: '10px',
                                bottom: '20px',
                                width: '180px',
                                minHeight: '140px',
                                zIndex: 1
                            }}
                        />
                    </div>
                </div>

                {/* 'I will...' Section at the bottom */}
                <footer className="mt-1" style={{ paddingBottom: '30px' }}>
                    <div style={{
                        backgroundColor: '#FABF8F',
                        color: 'black',
                        textAlign: 'center',
                        padding: '8px 0',
                        fontWeight: 'bold',
                        fontSize: '14.82px',
                        marginBottom: '20px'
                    }}>
                        I will...
                    </div>
                    <div>
                        <LabeledLine label="I will take guidance from" value={mappedData.guidance_from}
                            endLabel="to achieve my ambition." />
                        <LabeledLine label="I think this person will help me by" value={mappedData.help_by_person} />
                        <LabeledLine label="I will learn new" value={mappedData.learn_new} />
                        <LabeledLine label="I will feel" value={mappedData.feel_when_achieved}
                            endLabel="when I achieve my ambition." />
                        <LabeledLine label="My parents will feel" value={mappedData.parents_feel_when_achieved}
                            endLabel="when I achieve my ambition." />
                    </div>
                </footer>

            </div>
        </div>
    );
};

export default PartA3;